import { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import moment, { Moment } from 'moment';
import { AppState } from 'store';
import {
  clearSecretData,
  createClientCredential,
  deleteClientCredential,
  fetchClientCredentials,
  rotateClientSecret,
} from '../../../store/clientCredentials/actions';
import DatePicker from '../../../components/DatePicker/DatePicker';
import {
  selectAllClientCredentials,
  selectClientCredentialsLoading,
  selectNewlyCreatedCredential,
  selectRotatedCredential,
} from '../../../store/clientCredentials/selectors';
// Components
import { NoResultsCard } from 'components/NoResultsCard';
import { BoardListTable } from './BoardListTable';
import { LoadingCards } from 'components/LoadingCard';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useCanAccessClientCredentials } from 'hooks/utils';
import './ClientCredentialsPage.scss';

const ClientCredentialsPage: React.FC = () => {
  const dispatch = useDispatch();
  const credentials = useSelector((state: AppState) => selectAllClientCredentials(state));
  const isLoading = useSelector((state: AppState) => selectClientCredentialsLoading(state));
  const newlyCreated = useSelector((state: AppState) => selectNewlyCreatedCredential(state));
  const rotatedCredential = useSelector((state: AppState) => selectRotatedCredential(state));
  const hasAccess = useCanAccessClientCredentials();

  const [pageSize] = useState(30);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [validMonthLength, setValidMonthLength] = useState(2);
  const [createdDate, setCreatedDate] = useState<string>('');
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [secretData, setSecretData] = useState<{ clientID: string; clientSecret: string } | null>(null);

  useEffect(() => {
    dispatch(fetchClientCredentials(0, pageNumber, pageSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (credentials?.Pagination) {
      setPageCount(credentials.Pagination.TotalPages);
    }
  }, [credentials]);

  // Show newly created credential in dialog
  useEffect(() => {
    if (newlyCreated) {
      setSecretData(newlyCreated);
      setShowSecretDialog(true);
      // Refresh the list
      dispatch(fetchClientCredentials(0, pageNumber, pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newlyCreated]);

  // Show rotated credential in dialog
  useEffect(() => {
    if (rotatedCredential) {
      setSecretData(rotatedCredential);
      setShowSecretDialog(true);
      // Refresh the list
      dispatch(fetchClientCredentials(0, pageNumber, pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotatedCredential]);

  // Redirect to dashboard if user doesn't have access
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
    dispatch(fetchClientCredentials(0, newPage, pageSize));
  };

  const isPending = isLoading && <LoadingCards />;

  // Show this when we don't have any results.
  const noResults: ReactElement = !credentials?.Data?.length && !isLoading && <NoResultsCard />;

  // Show this when we have results.
  const hasResults = !!credentials?.Data?.length && (
    <BoardListTable
      results={credentials?.Data}
      onDelete={(id) => {
        if (window.confirm('Are you sure you want to delete this client credential?')) {
          dispatch(deleteClientCredential(id));
        }
      }}
      onRotate={(clientId) => {
        if (window.confirm('Are you sure you want to rotate this client secret? The old secret will be invalidated.')) {
          dispatch(rotateClientSecret({ client_id: clientId }));
        }
      }}
    />
  );

  const handleChangeValidMonthLength = (value: number) => {
    setValidMonthLength(value);
  };

  const handleCreate = () => {
    dispatch(
      createClientCredential({
        createdDate: createdDate,
        validMonthLength: validMonthLength,
      }),
    );
    setShowForm(false);
  };

  const openNewClientCredential = () => {
    // Set current date in the format yyyy-mm-ddT00:00:00.000Z
    const currentDate = moment().format('YYYY-MM-DD') + 'T00:00:00.000Z';
    setCreatedDate(currentDate);
    setShowForm(true);
  };

  const handleCopyClientID = () => {
    if (secretData?.clientID) {
      navigator.clipboard.writeText(secretData.clientID);
    }
  };

  const handleCopySecret = () => {
    if (secretData?.clientSecret) {
      navigator.clipboard.writeText(secretData.clientSecret);
    }
  };

  const handleCloseSecretDialog = () => {
    setShowSecretDialog(false);
    setSecretData(null);
    dispatch(clearSecretData());
  };

  interface FormValues {
    createdDate: Moment | null;
  }

  const initialValues: FormValues = {
    createdDate: moment(),
  };

  const handleSubmit = () => {
    // Form submission is handled by the Create button, not by Formik submit
  };

  return (
    <Grid component="section" className="summary-container">
      <Grid size={{ xs: 12 }}>
        <Box className="header-container">
          <h1 className="title">API Management</h1>

          <Box className="header-actions">
            <Button aria-label="add" onClick={openNewClientCredential}>
              <AddIcon /> New Client Secret
            </Button>
            {hasResults && <span>{credentials?.Data?.length} results</span>}
          </Box>
        </Box>
      </Grid>
      <Grid size={{ xs: 3 }} sx={{ alignContent: 'space-between' }}>
        {showForm && (
          <Box className="form-container">
            <FormControl variant="outlined" size="small">
              <InputLabel id="period-label">Valid Period</InputLabel>
              <Select
                labelId="period-label"
                value={String(validMonthLength)}
                onChange={(e) => handleChangeValidMonthLength(Number(e.target.value))}
                label="Valid Period"
              >
                <MenuItem value="1">1 month</MenuItem>
                <MenuItem value="2">2 months</MenuItem>
                <MenuItem value="3">3 months</MenuItem>
                <MenuItem value="6">6 months</MenuItem>
                <MenuItem value="12">12 months</MenuItem>
              </Select>
            </FormControl>
            <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
              {() => (
                <Form>
                  <Field name="createdDate">
                    {({ field }: FieldProps<Moment>) => (
                      <DatePicker
                        field={field}
                        formikKey="createdDate"
                        onChange={(_name, dateValue) => {
                          // Format date as yyyy-mm-ddT00:00:00.000Z
                          const formattedDate = dateValue.format('YYYY-MM-DD') + 'T00:00:00.000Z';
                          setCreatedDate(formattedDate);
                        }}
                        label="Created Date"
                        fullWidth
                        required
                      />
                    )}
                  </Field>
                </Form>
              )}
            </Formik>
            <Box className="button-group">
              <Button className="primary" onClick={handleCreate}>
                Create
              </Button>
              <Button className="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </Grid>

      <Grid size={{ xs: 12 }}>
        {hasResults || isPending || noResults}
        {pageCount > 1 && (
          <Box className="pagination-container">
            <Pagination
              count={pageCount}
              page={pageNumber}
              onChange={handlePageChange}
              siblingCount={1}
              boundaryCount={1}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Grid>

      {/* Secret Display Dialog */}
      <Dialog open={showSecretDialog} onClose={handleCloseSecretDialog} maxWidth="md" fullWidth>
        <DialogTitle>Client Credentials Created Successfully</DialogTitle>
        <DialogContent>
          <Box className="secret-display-box">
            <Typography variant="body1" className="warning-message" gutterBottom>
              <strong>Important:</strong> This is the only time you will see the client secret. Please copy it now and
              store it securely.
            </Typography>
          </Box>
          <Box className="secret-display-box">
            <Typography variant="subtitle2">Client ID:</Typography>
            <Typography variant="body1" className="secret-code-block">
              {secretData?.clientID || 'Not available'}
            </Typography>
          </Box>
          <Box className="secret-display-box">
            <Typography variant="subtitle2">Client Secret:</Typography>
            <Typography variant="body1" className="secret-code-block">
              {secretData?.clientSecret || 'Not available'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyClientID} color="primary">
            Copy Client ID
          </Button>
          <Button onClick={handleCopySecret} color="primary">
            Copy Secret
          </Button>
          <Button onClick={handleCloseSecretDialog} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      <LoadingOverlay isOpen={isLoading} />
    </Grid>
  );
};

export default ClientCredentialsPage;
